// ===========================================
// Line Item Week Builder
// ===========================================
// Pure helpers for the manual-invoice week selector's STATE: which weeks of each
// enrollment bucket the admin has chosen, how (a starting week or week by week),
// and whether a bucket bills at a custom price. Shared by CreateInvoiceDialog and
// EditInvoiceDialog. Kept framework-free so it is trivially unit-testable.
//
// ⚠️ The browser does NOT price or word the invoice (since 2026-09-25). It sends
// the selection (`toWeekSelectionPayload`) — weeks, a starting week, and a
// custom rate only where the admin switched one on — and the SERVER prices it
// from the pricing matrix and writes the lines
// (backend services/invoice/billing/weekSelection.ts). The dialogs show the
// server's own preview of those lines. Never re-add price arithmetic or line
// wording here: two implementations drift, and the browser's copy is the one
// that would be wrong.

import {
  BillableEnrollment,
  BillableBucket,
  CreateInvoiceItemInput,
  WeekSelectionPayload,
} from '../../api/invoices.api';
import { effectiveStartWeek } from '../../utils/dateUtils';

/**
 * Mirrors the backend `invoiceItemSchema` description `.max(200)`
 * (validators/invoice.validators.ts). The server refuses an overlong line when
 * creating; the dialogs flag it on the preview first.
 */
export const MAX_LINE_ITEM_DESCRIPTION_LENGTH = 200;

/** A bucket plus the weeks the admin has selected for it (subset of sessionWeeks). */
export interface SelectedBucket {
  kind: BillableBucket['kind'];
  selectedWeeks: number[];
  /** The pricing-matrix price per week (0 when the matrix has none). Display only. */
  unitPrice: number;
  /**
   * The admin's own per-week price, set only after switching "Custom price" on;
   * null = bill at the matrix price. Sent to the server as an explicit override.
   */
  customRate: number | null;
}

/**
 * How the admin chooses weeks for one enrollment row:
 *  - START_WEEK: pick the week the student started; every week each bucket runs
 *    from then to the end of term is selected for them (the mid-term joiner case,
 *    and the only choice a hybrid needs — no separate group/individual grids).
 *  - MANUAL: toggle each bucket's weeks individually (a missed week, a leaver).
 * `selectedWeeks` stays the single source of truth in BOTH modes; the start week
 * only DERIVES it, on an explicit click. Switching modes never changes it.
 */
export type WeekSelectionMode = 'START_WEEK' | 'MANUAL';

/** One enrollment row in the week selector: its selections plus how they were chosen. */
export interface SelectedEnrollment {
  enrollment: BillableEnrollment;
  buckets: SelectedBucket[];
  mode: WeekSelectionMode;
  /** The chosen starting week; null until the admin picks one. Enables the start date. */
  startWeek: number | null;
  /**
   * The term's last week (`termWeekCount`), or null for no bound. A session week
   * past it has no tile in the picker, so a start-week click must never select it
   * (a shortened EVERY_WEEK band term can strand one — CLAUDE.md #173).
   */
  lastWeek: number | null;
  /**
   * The "Save as {student}'s start week" box (create dialog only). Off until ticked;
   * it only matters while `startWeekWriteBack(sel).changed`.
   */
  saveStartWeek?: boolean;
}

/**
 * Seed a SelectedEnrollment from a billable enrollment: NO weeks selected yet,
 * matrix prices, no custom prices, starting-week mode with no start chosen.
 * `lastWeek` is the term's week count (BillableContext `termWeeks`); omit it for
 * no bound.
 *
 * We deliberately don't pre-select any weeks — the admin chooses exactly what to
 * bill (we shouldn't presume the full term, or any weeks at all). Picking a start
 * week is that explicit choice, not a default. Past weeks ARE selectable here
 * (billing is often after the fact).
 *
 * The ONE exception (mid-term start week, 2026-09-30): a student whose STORED start
 * (`max(enrolment start, class start)`) is later than their first session week is seeded
 * exactly as if the admin had clicked that week as the "Starting week" — through
 * `applyStartWeek`, the click's own path — so the invoice bills from where they actually
 * joined. It is a derivation from a stored fact, never an arbitrary pre-selection; the
 * admin can click another week or clear it. A student who starts WITH the class has
 * `sessionWeeks` beginning at the class start already, so nothing is seeded.
 */
export function seedSelection(
  enrollment: BillableEnrollment,
  lastWeek: number | null = null
): SelectedEnrollment {
  const base = emptySelection(enrollment, lastWeek);
  const target = storedStartSessionWeek(enrollment, lastWeek);
  return target == null ? base : applyStartWeek(base, target);
}

/** A student's stored effective start: the later of their own and the class's (null = week 1). */
export function storedEffectiveStart(enrollment: BillableEnrollment): number {
  return effectiveStartWeek({ startWeek: enrollment.classStartWeek }, { startWeek: enrollment.startWeek });
}

/**
 * The session week a stored start seeds, or null when nothing should be seeded: the first
 * session week at or after the stored start, and only when the stored start is LATER than
 * the enrolment's first session week. A stored start past the last session week seeds
 * nothing (there is no tile to click).
 */
function storedStartSessionWeek(
  enrollment: BillableEnrollment,
  lastWeek: number | null
): number | null {
  const weeks = unionSessionWeeks(enrollment, lastWeek);
  if (weeks.length === 0) return null;
  const stored = storedEffectiveStart(enrollment);
  if (stored <= weeks[0]) return null;
  return weeks.find((w) => w >= stored) ?? null;
}

/**
 * Should the "Save as {student}'s start week" box show, and what would it save? Compares
 * EFFECTIVE starts (null and the class's own start are equal), and treats the week a stored
 * start seeded as unchanged. `startWeek` is what to PATCH: the chosen week, or null when it
 * is at or before the class start (the server refuses a start the class already has).
 */
export function startWeekWriteBack(sel: SelectedEnrollment): {
  changed: boolean;
  startWeek: number | null;
} {
  const { enrollment } = sel;
  if (sel.startWeek == null) return { changed: false, startWeek: null };
  const classStart = enrollment.classStartWeek ?? 1;
  const chosenEffective = effectiveStartWeek({ startWeek: enrollment.classStartWeek }, { startWeek: sel.startWeek });
  // Unchanged when the chosen start and the stored one bill from the SAME first session.
  // Comparing week numbers alone offered to save week 2 for a student who started with a
  // class whose first session is week 2 (a sparse hybrid or band pattern) — /qa Phase 3.
  const weeks = unionSessionWeeks(enrollment, sel.lastWeek);
  const firstSessionFrom = (week: number) => weeks.find((w) => w >= week);
  const unchanged =
    chosenEffective === storedEffectiveStart(enrollment) ||
    firstSessionFrom(chosenEffective) === firstSessionFrom(storedEffectiveStart(enrollment));
  return {
    changed: !unchanged,
    startWeek: sel.startWeek > classStart ? sel.startWeek : null,
  };
}

function emptySelection(
  enrollment: BillableEnrollment,
  lastWeek: number | null
): SelectedEnrollment {
  return {
    enrollment,
    buckets: enrollment.buckets.map((b) => ({
      kind: b.kind,
      selectedWeeks: [],
      unitPrice: b.unitPrice ?? 0,
      customRate: null,
    })),
    mode: 'START_WEEK',
    startWeek: null,
    lastWeek,
  };
}

/** The per-week price a bucket will bill at: the custom price if on, else the matrix. */
export function effectiveRate(bucket: SelectedBucket): number {
  return bucket.customRate ?? bucket.unitPrice;
}

/** Is week `w` inside the term's picker range? */
function inTerm(w: number, lastWeek: number | null): boolean {
  return w >= 1 && (lastWeek == null || w <= lastWeek);
}

/**
 * Every week this enrollment runs a session in ANY bucket, ascending — bounded to
 * `1..lastWeek` when given, so it never offers a week the picker has no tile for.
 */
export function unionSessionWeeks(
  enrollment: BillableEnrollment,
  lastWeek: number | null = null
): number[] {
  const weeks = new Set<number>();
  for (const b of enrollment.buckets) {
    for (const w of b.sessionWeeks) if (inTerm(w, lastWeek)) weeks.add(w);
  }
  return [...weeks].sort((a, b) => a - b);
}

/** Each bucket's weeks from `startWeek` to the end of term (empty for null). */
function weeksFromStart(sel: SelectedEnrollment, startWeek: number | null): number[][] {
  return sel.buckets.map((_, i) => {
    const sessionWeeks = sel.enrollment.buckets[i]?.sessionWeeks ?? [];
    return startWeek == null
      ? []
      : sessionWeeks
          .filter((w) => w >= startWeek && inTerm(w, sel.lastWeek))
          .sort((a, b) => a - b);
  });
}

/**
 * Select, in every bucket, each session week from `startWeek` to the end of term
 * (null clears the selection). Buckets pair with `enrollment.buckets` by index,
 * as everywhere else in the week selector. A bucket that does not run from
 * `startWeek` onwards ends up empty and bills nothing. Prices are untouched.
 */
export function applyStartWeek(
  sel: SelectedEnrollment,
  startWeek: number | null
): SelectedEnrollment {
  const derived = weeksFromStart(sel, startWeek);
  // A DIFFERENT start week asks again whether to save it: a tick given for another week must
  // not come back already ticked (/qa Phase 3). Re-applying the same week keeps it.
  const rest = { ...sel };
  if (startWeek !== sel.startWeek) delete rest.saveStartWeek;
  return {
    ...rest,
    startWeek,
    buckets: sel.buckets.map((bucket, i) => ({ ...bucket, selectedWeeks: derived[i] })),
  };
}

/**
 * Switch a row between START_WEEK and MANUAL. The selection is ALWAYS kept: a mode
 * is only a way of editing it, and switching must never change what is billed.
 * (It used to re-derive "from week N" on the way back to START_WEEK, silently
 * re-adding a week the admin had just unticked — QA M2, 2026-09-25.) A selection
 * that no longer matches its start week is reported by `customSelection`, and
 * the row says so.
 */
export function setSelectionMode(
  sel: SelectedEnrollment,
  mode: WeekSelectionMode
): SelectedEnrollment {
  return mode === sel.mode ? sel : { ...sel, mode };
}

/** How a hand-edited selection differs from "every week from the start week". */
export interface CustomSelection {
  /** Weeks the start week would bill that are NOT selected (e.g. a missed week). */
  unticked: number[];
  /** Selected weeks the start week would NOT bill (e.g. ticked before the start). */
  added: number[];
}

/**
 * null when the selection is exactly what the start week produces (including the
 * untouched "nothing chosen yet" state); otherwise the week numbers that differ,
 * de-duplicated across buckets. With no start week, every selected week counts
 * as `added` — they were picked individually.
 */
export function customSelection(sel: SelectedEnrollment): CustomSelection | null {
  const derived = weeksFromStart(sel, sel.startWeek);
  const unticked = new Set<number>();
  const added = new Set<number>();
  sel.buckets.forEach((bucket, i) => {
    const want = new Set(derived[i]);
    const have = new Set(bucket.selectedWeeks);
    for (const w of want) if (!have.has(w)) unticked.add(w);
    for (const w of have) if (!want.has(w)) added.add(w);
  });
  if (unticked.size === 0 && added.size === 0) return null;
  const sorted = (s: Set<number>) => [...s].sort((a, b) => a - b);
  return { unticked: sorted(unticked), added: sorted(added) };
}

/** The union of every bucket's selected weeks, ascending. */
export function selectedUnion(sel: SelectedEnrollment): number[] {
  return [...new Set(sel.buckets.flatMap((b) => b.selectedWeeks))].sort((a, b) => a - b);
}

/** Does any selected bucket have weeks but no price to bill them at? */
export function hasUnpricedBucket(selections: SelectedEnrollment[]): boolean {
  return selections.some((sel) =>
    sel.buckets.some((b) => b.selectedWeeks.length > 0 && !(effectiveRate(b) > 0))
  );
}

/**
 * The request the server prices: for each row with any week selected, its
 * buckets' weeks, its starting week, and a custom rate ONLY for a bucket whose
 * "Custom price" is switched on. No matrix price is sent — the server looks it
 * up itself, so a stale or tampered browser price can never be billed.
 */
export function toWeekSelectionPayload(selections: SelectedEnrollment[]): WeekSelectionPayload[] {
  return selections
    .filter((sel) => sel.buckets.some((b) => b.selectedWeeks.length > 0))
    .map((sel) => ({
      enrollmentId: sel.enrollment.enrollmentId,
      startWeek: sel.startWeek,
      buckets: sel.buckets.map((b) => ({
        kind: b.kind,
        weeks: [...b.selectedWeeks].sort((x, y) => x - y),
        ...(b.customRate != null ? { customRate: b.customRate } : {}),
      })),
    }));
}

/** Items whose description exceeds the backend's 200-character limit. */
export function overlongItems(items: CreateInvoiceItemInput[]): CreateInvoiceItemInput[] {
  return items.filter((i) => i.description.length > MAX_LINE_ITEM_DESCRIPTION_LENGTH);
}
